import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@/api/repository/axios';
import type { ApplicableCoupon, SeminarClaimableCoupon } from '@/api/repository/types/types';
import { discountRate, errorMessageOf, unwrapList } from '@/components/domain/coupon/couponFormat';
import { notifyCouponCountChanged } from '@/components/domain/coupon/useMyCouponCount';

/**
 * 세미나 상세의 쿠폰 — "쿠폰 N장" 카드 목록 + 참가비 줄의 쿠폰 적용가(받은/받을 수 있는 쿠폰 중 최대 할인).
 * 서버에 쿠폰 API 가 아직 없거나 실패하면 쿠폰 없음(빈 목록)으로 동작한다.
 */

export interface BestCouponPrice {
  discount: number;
  rate: number;
  price: number;
  couponName: string;
  /** 아직 안 받은 쿠폰 기준이면 true — "쿠폰은 아래에서 받을 수 있어요" */
  claimNeeded: boolean;
}

interface Options {
  seminarId?: string;
  enabled: boolean;
  isLoggedIn: boolean;
  listPrice: number;
}

/** 받은 쿠폰의 지금 상태 — 쿠폰 카드 버튼 문구 */
export type ClaimedCouponState = 'ready' | 'reserved' | 'used';

export const useSeminarCoupons = ({ seminarId, enabled, isLoggedIn, listPrice }: Options) => {
  const [coupons, setCoupons] = useState<SeminarClaimableCoupon[]>([]);
  const [applicable, setApplicable] = useState<ApplicableCoupon[]>([]);
  // applicable 응답을 받았는지 — 못 받았으면 받은 쿠폰 상태를 추측하지 않는다(모두 '받음')
  const [applicableLoaded, setApplicableLoaded] = useState(false);
  const [claimingId, setClaimingId] = useState<number | null>(null);

  const refresh = useCallback(async () => {
    if (!seminarId || !enabled) {
      setCoupons([]);
      setApplicable([]);
      return;
    }
    try {
      const res = await api.coupon.listForSeminar(seminarId);
      setCoupons(res?.code === 0 ? unwrapList<SeminarClaimableCoupon>(res.result) : []);
    } catch {
      setCoupons([]);
    }
    if (!isLoggedIn) {
      setApplicable([]);
      setApplicableLoaded(false);
      return;
    }
    try {
      const res = await api.coupon.applicable(seminarId);
      const ok: boolean = res?.code === 0;
      setApplicable(ok ? unwrapList<ApplicableCoupon>(res.result) : []);
      setApplicableLoaded(ok);
    } catch {
      setApplicable([]);
      setApplicableLoaded(false);
    }
  }, [seminarId, enabled, isLoggedIn]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /** 받기 — 성공이면 null, 실패면 사용자에게 보일 문구 */
  const claim = useCallback(async (couponId: number): Promise<string | null> => {
    if (!seminarId) return '세미나 정보를 찾을 수 없어요.';
    setClaimingId(couponId);
    try {
      const res = await api.coupon.claim(seminarId, couponId);
      if (res?.code !== 0) return res?.message || '쿠폰을 받지 못했어요.';
      setCoupons((prev: SeminarClaimableCoupon[]) => prev.map((c: SeminarClaimableCoupon) => (
        c.couponId === couponId
          ? { ...c, claimed: true, remaining: typeof c.remaining === 'number' ? Math.max(0, c.remaining - 1) : c.remaining }
          : c
      )));
      notifyCouponCountChanged();
      void refresh();
      return null;
    } catch (error: unknown) {
      void refresh();
      return errorMessageOf(error, '쿠폰을 받지 못했어요.');
    } finally {
      setClaimingId(null);
    }
  }, [seminarId, refresh]);

  const best: BestCouponPrice | null = useMemo(() => {
    if (!enabled || listPrice <= 0) return null;
    let bestDiscount: number = 0;
    let name: string = '';
    let claimNeeded: boolean = false;
    applicable.forEach((c: ApplicableCoupon) => {
      if (c.applicable && c.discountAmount > bestDiscount) {
        bestDiscount = c.discountAmount;
        name = c.name;
        claimNeeded = false;
      }
    });
    // 받은 쿠폰은 위 applicable(실제로 지금 쓸 수 있는 것)로만 센다 — 이미 쓴·결제 중인 쿠폰이 적용가를 부풀리지 않게
    coupons.forEach((c: SeminarClaimableCoupon) => {
      if (c.claimed) return;
      const discount: number = c.previewDiscount ?? 0;
      const soldOut: boolean = c.soldOut === true || c.remaining === 0;
      if (!soldOut && discount > bestDiscount) {
        bestDiscount = discount;
        name = c.name;
        claimNeeded = true;
      }
    });
    if (bestDiscount <= 0 || bestDiscount >= listPrice) return null;
    return {
      discount: bestDiscount,
      rate: discountRate(bestDiscount, listPrice),
      price: listPrice - bestDiscount,
      couponName: name,
      claimNeeded,
    };
  }, [enabled, listPrice, applicable, coupons]);

  /** 받은 쿠폰이 지금 쓸 수 있는지·결제 중인지·이미 썼는지(applicable 목록에 없으면 사용함) */
  const claimedStateOf = useCallback((coupon: SeminarClaimableCoupon): ClaimedCouponState => {
    if (!applicableLoaded) return 'ready';
    const mine: ApplicableCoupon | undefined = applicable.find((a: ApplicableCoupon) =>
      (coupon.issueId != null && a.issueId === coupon.issueId) || a.couponId === coupon.couponId);
    if (!mine) return 'used';
    if (!mine.applicable && /쓰는 중|결제/.test(mine.reason ?? '')) return 'reserved';
    return 'ready';
  }, [applicable, applicableLoaded]);

  return { coupons, best, claim, claimingId, refresh, claimedStateOf };
};
